<template>
	<div ref="nodeContainer" class="layout-container">
		<transition name="el-fade-in">
			<template v-if="isLoading"></template>
			<Empty v-else-if="isError" :text="i18n.t('enterprise.404-error')" />
			<template v-else>
				<template v-if="$route.meta.source === 'pureModule'">
					<router-view :key="$route.fullPath" />
				</template>
				<SmartLayoutC
					v-else-if="supportedTemplates.includes(currentLayoutType)"
					compId="layout_wrapper"
					:mode="layoutMode"
				/>
				<div v-else>{{ unSupportedValue }}</div>
			</template>
		</transition>

		<span class="font-ready font-HYYakuHei">HYYakuHei</span>
	</div>
</template>

<script>
export default {
	name: 'DynamicLayout'
};
</script>
<script setup>
import { computed, provide, onMounted, watch, getCurrentInstance, ref, onBeforeUnmount } from 'vue';
import { useNavigation } from '@rebuild/hooks/useNavigation';
import { useCoreData, useFuncStep } from '@rebuild/hooks/data/useCoreData';
import { getIrInputStoreId, isErrorValue } from '@/utils/api';
import { useRoute, useStore } from '@/utils/vue-api';
import i18n, { changeLocaleLang } from '@/locales';
import { useRedirect, useCommonData } from '@/hooks';
import SmartLayoutC from '@rebuild/components/SmartLayoutC.vue';
import { useFlow } from '@rebuild/hooks/useFlow';
import { useFlowX } from '@rebuild/hooks/useFlowX';
import { usePageWithLanguage } from '@rebuild/materials/shared/layout_nav/surf/usePageWithLanguage';
import { useEventIntercept } from '@/rebuild/hooks/useEventIntercept';
import { useIframeMessage } from '@/rebuild/hooks/message';
import { TEMPLATE_MAP } from '@/store';

const route = useRoute();
const store = useStore();

const { isDomainAccessSession } = window.__COMEIN_IR_STORE__;
const waitForConfigLoaded = () => {
	return new Promise(resolve => {
		const configId = window.__IRSTORE_CONFIG?.id;
		if (configId) {
			resolve(configId);
			return;
		}
		const handleConfigLoaded = event => {
			const configId = event.detail?.id;
			resolve(configId);
		};
		window.addEventListener('IRSTORE_CONFIG_LOADED', handleConfigLoaded);
	});
};

const { currentTemplate } = useNavigation();
const currentLayoutType = computed(() => currentTemplate.value);
provide('TEMPLATE_TYPE', currentLayoutType);

const displayMenus = ref();
provide('TEMPLATE_MENUS', displayMenus);

const {
	isLoading,
	showLoading,
	hideLoading,

	loadIRBasicInfo,
	loadIRPageData,
	loadUIConfigWithTmplId
} = useCoreData();

const isError = ref(false);
const { runSteps, wrapStep } = useFuncStep();

onMounted(async () => {
	const isDomainSession = isDomainAccessSession?.();
	if (isDomainSession) {
		// 必须等待配置加载完成，才能确保拿到 storeId
		await waitForConfigLoaded();
	}

	// 模板类型入口判断
	store.dispatch('switchTemplate', TEMPLATE_MAP[currentLayoutType.value]);

	showLoading();
	const { hasError } = await runSteps([
		wrapStep(loadIRBasicInfo),
		wrapStep(loadUIConfigWithTmplId),
		wrapStep(loadIRPageData)
	]);
	if (hasError) {
		isError.value = true;
	}
	hideLoading();
});

const { composeFlowMenu } = useFlow();
const { composeFlowXMenu } = useFlowX();
const { updateIdMapInPageList } = usePageWithLanguage();
const setTemplateMenus = () => {
	const normalMenus = store.getters.getNormalMenus || [];
	if (currentLayoutType.value === 'flow') {
		displayMenus.value = composeFlowMenu(normalMenus);
	} else if (currentLayoutType.value === 'flowX') {
		displayMenus.value = composeFlowXMenu(normalMenus);
	} else if (currentLayoutType.value === 'slide' || currentLayoutType.value === 'slideM') {
		displayMenus.value = normalMenus;
	} else if (currentLayoutType.value === 'surf') {
		displayMenus.value = normalMenus;
		updateIdMapInPageList();
	} else {
		displayMenus.value = normalMenus;
	}
};
watch(
	() => store.getters.getNormalMenus,
	() => {
		setTemplateMenus();
	}
);

const isMobileMode = computed(() => store.getters.isMobileMode);
const supportedTemplates = Object.keys(TEMPLATE_MAP);
const unSupportedValue = '<Unknown>';
const layoutMode = computed(() => (isMobileMode.value ? 'mob-basic' : 'basic'));

// 强刷一轮语言版本
const forceRefreshLang = () => {
	// 同步覆盖语言缓存
	if (route.query.lang) changeLocaleLang(route.query.lang);
};
forceRefreshLang();

const getAccessMode = computed(() => store.state.accessMode);
const { checkEntryUrl, replaceToEntryUrl, replaceLocation, replaceLocationToHome } = useRedirect();

watch(
	() => getAccessMode.value,
	val => {
		sessionStorage.setItem('accessMode', val);

		if (isSurfTmpl.value) return; // surf 模板跳过强制重定向

		// TODO
		// 这个强刷逻辑并非必要，只需要动态控制 Smart*C 组件的 mode 属性，就能实现组件重载
		// 但这属于破坏性改动，需要考虑功能回归
		const hasEntryUrl = checkEntryUrl();
		if (hasEntryUrl) {
			replaceToEntryUrl(hasEntryUrl);
		} else {
			replaceLocation();
		}
	}
);
watch(
	() => i18n.locale,
	lang => {
		// TODO
		// 这里是一个已知的遗留问题，且所有模板都存在，由于不同语言需应用不同的页面数据，会产生不同的菜单 ID，
		// 如果是按照入口 URL 去做页面刷新，会导致菜单 ID 不匹配，进而导致页面无法正常显示。

		const hasEntryUrl = checkEntryUrl();
		if (hasEntryUrl) {
			replaceToEntryUrl(hasEntryUrl, lang);
		} else {
			replaceLocationToHome(lang);
		}
	}
);

const vm = getCurrentInstance();
watch(
	() => vm.proxy.$route,
	newRoute => {
		syncSelectActiveModule(newRoute);
		syncMobileTitle(newRoute);
	}
);

// 自适应情况下的标题同步
const { getOrganizationName, isSurfTmpl, getFullCode } = useCommonData();
const syncMobileTitle = newRoute => {
	const isMobile = getAccessMode.value === 'mobile';
	if (!isMobile) return;

	let title;
	const isHomeView = newRoute.name.includes('home');
	if (isHomeView) {
		if (getFullCode.value === 'hk02595') {
			title = '劲方医药-企业资讯小程序';
		} else {
			title = i18n.t('enterprise.irstore', { name: getOrganizationName.value });
		}
	} else {
		const { menuId } = newRoute.params;
		const normalMenus = store.getters.getNormalMenus;
		const findMenuInfo = normalMenus.find(item => item.id == menuId);
		title = findMenuInfo?.name;
	}

	if (title) {
		document.title = title;
	}
};

// 同步模块选择记录
const syncSelectActiveModule = newRoute => {
	const { params } = newRoute;
	const { menuId, moduleId } = params;

	if (moduleId) {
		store.commit('updateSelectModule', moduleId);
	} else if (menuId) {
		const normalMenus = store.getters.getNormalMenus;
		const findMenuInfo = normalMenus.find(item => item.id == menuId);
		const findFirstModule = findMenuInfo?.moduleVOS?.[0];
		store.commit('updateSelectModule', findFirstModule?.moduleId);
	} else {
		store.commit('updateSelectModule', null);
	}
};

// 数据重置
const resetStoreData = () => {
	const storeId = getIrInputStoreId();
	if (isErrorValue('storeId', storeId)) {
		store.commit('setColumnData', {});
		store.commit('setCommonData', {
			data: {}
		});
	}
};
resetStoreData();

// 预览信息响应
const { addIframeMessageListener, removeIframeMessageListener } = useIframeMessage();

// 事件响应拦截
const { nodeContainer, addEventIntercept, removeEventIntercept } = useEventIntercept();

onMounted(() => {
	addIframeMessageListener();
	addEventIntercept();
});
onBeforeUnmount(() => {
	removeIframeMessageListener();
	removeEventIntercept();
});
</script>

<style lang="less">
@import './surf/surf-variables.less';
@import './surf/surf-transition.less';
@import './surf/surf-global-styles.less';

:root {
	--pc-layout-min-width: 1200px;
}
// 根据适配公式得来：w1440时, rem的转换比例为 1
@media screen and (max-width: 1440px) and (min-width: 760px) {
	.ex2rem-block-padding {
		padding-left: 12px !important;
		padding-right: 12px !important;
	}
	.ex2rem-min-w1200-layout-next {
		min-width: 1200px;
	}
}
// UI：固定宽度不适配
@media screen and (max-width: 1440px) and (min-width: 1200px) {
	.ex2rem-slide-menu-width {
		width: 200px !important;
	}
}

// 在开始阶段触发字体包加载
.font-ready {
	display: none;
	position: fixed;
	top: 0;
	left: 0;
}
.font-HYYakuHei {
	font-family: HYYakuHei;
}
</style>
